import React, { useEffect, useState, useMemo } from "react";
import {
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  Edit3,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import DeleteAlertModal from "../../common/DeleteAlertModal";
import { uploadToCloudinary } from "../../../utils/cloudinary"; 

// Skeleton Loader for table rows
const SkeletonRow = ({ cols = 7 }) => (
  <tr className="border-b border-gray-100">
    {[...Array(cols)].map((_, j) => (
      <td key={j} className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse" />
      </td>
    ))}
  </tr>
);

// Edit User Modal Component
const EditUserModal = ({ isOpen, onClose, user, onSaved }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "",
    imageUrl: "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // populate form when modal opens
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        gender: user.gender || "",
        imageUrl: user.imageUrl || "",
      });
    }
  }, [user]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  // Cloudinary upload handler
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const data = await uploadToCloudinary(file);
      const url = typeof data === "string" ? data : data.secure_url || data.url || "";
      setForm((p) => ({ ...p, imageUrl: url }));
      toast.success("Image uploaded successfully");
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Update user API
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/customers/update-by-user-id/${user._id}`,
        payload
      );

      if (res?.data?.success) {
        toast.success("User updated successfully");
        onSaved();
        onClose();
      } else {
        toast.error(res?.data?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-xl p-6 shadow-lg animate-fadeIn">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-800">Edit Customer</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl">
            &times;
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              placeholder="First name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Last name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <select
              name="gender"
              value={form.gender}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Profile Image</label>
            <div className="flex items-center gap-4">
              <label className="cursor-pointer rounded-md bg-gray-100 border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 transition">
                {uploading ? "Uploading..." : "Choose Image"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
              </label>
              {form.imageUrl ? (
                <img
                  src={form.imageUrl}
                  alt="Preview"
                  className="h-12 w-12 rounded-full object-cover border"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-400 border">
                  No Image
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-white ${
                saving ? "bg-blue-400 cursor-not-allowed" : "bg-[#b07d17] hover:bg-[#8c6312]"
              }`}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const AdminCustomerList = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [editUser, setEditUser] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteState, setDeleteState] = useState({
    isOpen: false,
    user: null,
    loading: false,
  });

  // Corrected fetchUsers
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/customers/get-all-users`);
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setCustomers(res.data.data);
      } else {
        toast.error(res?.data?.message || "Failed to load users");
      }
    } catch (err) {
      console.error("Fetch users error:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const name = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      return (
        name.includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q)
      );
    });
  }, [customers, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / entriesPerPage));
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(filteredCustomers.length, startIndex + entriesPerPage);
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  const openEdit = (user) => {
    setEditUser(user);
    setIsEditOpen(true);
  };
  const closeEdit = () => {
    setIsEditOpen(false);
    setEditUser(null);
  };

  const openDelete = (user) => setDeleteState({ isOpen: true, user, loading: false });
  const closeDelete = () => setDeleteState({ isOpen: false, user: null, loading: false });

  const confirmDelete = async () => {
    if (!deleteState.user) return;
    setDeleteState((s) => ({ ...s, loading: true }));
    try {
      const id = deleteState.user._id;
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}/customers/delete-user/${id}`);
      if (res?.data?.success) {
        toast.success("User deleted");
        await fetchUsers();
        closeDelete();
      } else {
        toast.error(res?.data?.message || "Delete failed");
        setDeleteState((s) => ({ ...s, loading: false }));
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Delete failed");
      setDeleteState((s) => ({ ...s, loading: false }));
    }
  };

  return (
    <div className="min-h-screen ">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
            <Users className="w-7 h-7 text-primaryColor" />
            Customers
          </h1>
        </div>

        {/* Search + Pagination Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:ring-primaryColor focus:outline-none"
            />
            <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>
          {/* <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show</label>
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1"
            >
              {[5, 10, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="text-sm text-gray-600">entries</label>
          </div> */}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">#</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Customer</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Gender</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Date Joined</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
                ) : currentCustomers.length > 0 ? (
                  currentCustomers.map((c, idx) => (
                    <tr key={c._id} className="border-b hover:bg-gray-50 transition">
                      <td className="px-4 py-4 text-sm text-gray-800">{startIndex + idx + 1}</td>
                      <td className="px-4 py-4 flex items-center gap-3">
                        <img
                          src={c.imageUrl || "/no-image.png"}
                          alt={c.firstName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {c.firstName} {c.lastName}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{c.email}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{c.gender || "—"}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{c.phone || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openEdit(c)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition"
                          >
                            <Edit3 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => openDelete(c)}
                            className="p-2 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-6 text-gray-500">
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && filteredCustomers.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {endIndex} of {filteredCustomers.length} customers
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))
                  .map((p) => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1 rounded-lg transition ${
                        currentPage === p
                          ? "bg-blue-500 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <EditUserModal
        isOpen={isEditOpen}
        onClose={closeEdit}
        user={editUser}
        onSaved={fetchUsers}
      />
      <DeleteAlertModal
        isOpen={deleteState.isOpen}
        onClose={closeDelete}
        dltType="customer"
        dltName={`${deleteState.user?.firstName || ""} ${deleteState.user?.lastName || ""}`}
        isLoading={deleteState.loading}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminCustomerList;
